// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EventDetailPageShell from './EventDetailPageShell';
import { doc, getDoc } from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { db } from '../../utils/firebase';
import { EVENT_DEFINITIONS_COLLECTION } from '../../utils/firestoreCollections';
import {
  resolveStoredImageUrl,
  eventHeaderImageFilename,
} from './eventImageUpload';
import useEventStorageImages from '../../hooks/useEventStorageImages';
import useEventDefinitionsList from '../../hooks/useEventDefinitionsList';
import EditEventDialog from './EditEventDialog';
import useEventFormDialog from './useEventFormDialog';
import { buildEventDetailViewModel } from './eventDetailViewModel';
import {
  EventDetailLoadingState,
  EventDetailErrorState,
  EventDetailInfoCard,
} from './EventDetailPageSections';

function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [eventRow, setEventRow] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [activeDetailTab, setActiveDetailTab] = useState(0);

  const {
    events,
    createEvent,
    updateEvent,
    removeFeaturedBeersFromEvent,
  } = useEventDefinitionsList();
  const {
    fileNames,
    imageUrlByName,
    loading: storageLoading,
  } = useEventStorageImages();

  const allowedImageChoices = useMemo(
    () => [...fileNames].sort((left, right) => left.localeCompare(right)),
    [fileNames],
  );

  const refreshEventDoc = useCallback(async () => {
    if (!eventId) return;
    try {
      setLoadingDoc(true);
      setLoadError(null);
      const ref = doc(db, EVENT_DEFINITIONS_COLLECTION, eventId);
      const snapshot = await getDoc(ref);
      if (!snapshot.exists()) {
        setEventRow(null);
        setLoadError('This event could not be found.');
        return;
      }
      setEventRow({ id: snapshot.id, ...snapshot.data() });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load event', err);
      Sentry.captureException(err);
      setLoadError('Failed to load this event.');
      setEventRow(null);
    } finally {
      setLoadingDoc(false);
    }
  }, [eventId]);

  useEffect(() => {
    refreshEventDoc();
  }, [refreshEventDoc]);

  const mergedEvent = useMemo(() => {
    if (!eventRow?.id) return eventRow;
    const fromList = events.find((row) => row.id === eventRow.id);
    // fromList first so eventRow (fresh getDoc) wins over cached list fields.
    return fromList ? { ...fromList, ...eventRow } : eventRow;
  }, [eventRow, events]);

  const handleSaved = useCallback(
    async ({ mode, id }) => {
      if (mode === 'add' && id) {
        navigate(`/events/${id}`, { replace: true });
        return;
      }
      await refreshEventDoc();
    },
    [navigate, refreshEventDoc],
  );

  const { editDialogProps, openEditDialog } = useEventFormDialog({
    allowedImageChoices,
    imagesLoading: storageLoading,
    createEvent,
    updateEvent,
    onSaved: handleSaved,
  });

  const handleOpenEdit = useCallback(() => {
    if (!mergedEvent) return;
    openEditDialog(mergedEvent);
  }, [mergedEvent, openEditDialog]);

  const goBackToEvents = useCallback(() => {
    navigate('/events');
  }, [navigate]);

  const heroUrl = useMemo(
    () =>
      resolveStoredImageUrl(
        imageUrlByName,
        eventHeaderImageFilename(mergedEvent),
      ),
    [imageUrlByName, mergedEvent],
  );
  const viewModel = useMemo(
    () => buildEventDetailViewModel(mergedEvent),
    [mergedEvent],
  );

  const featuredBeerIds = useMemo(() => {
    const raw = mergedEvent?.featuredBeers;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((beerId) => String(beerId ?? '').trim()).filter(Boolean);
  }, [mergedEvent?.featuredBeers]);

  const handleDetailTabChange = useCallback((event, newValue) => {
    setActiveDetailTab(newValue);
  }, []);

  const handleAfterFeaturedBeersRemoved = useCallback(async () => {
    await refreshEventDoc();
  }, [refreshEventDoc]);

  const editDisabled =
    !mergedEvent || loadingDoc || Boolean(loadError);

  const centerBlock = loadingDoc ? (
    <EventDetailLoadingState />
  ) : loadError ? (
    <EventDetailErrorState message={loadError} />
  ) : viewModel ? (
    <EventDetailInfoCard viewModel={viewModel} />
  ) : null;

  return (
    <DashboardLayout title={mergedEvent?.name || 'Event'}>
      <EventDetailPageShell
        activeDetailTab={activeDetailTab}
        centerBlock={centerBlock}
        editDisabled={editDisabled}
        featuredBeerIds={featuredBeerIds}
        handleAfterFeaturedBeersRemoved={handleAfterFeaturedBeersRemoved}
        handleDetailTabChange={handleDetailTabChange}
        handleOpenEdit={handleOpenEdit}
        heroUrl={heroUrl}
        loadError={loadError}
        loadingDoc={loadingDoc}
        mergedEvent={mergedEvent}
        removeFeaturedBeersFromEvent={removeFeaturedBeersFromEvent}
        goBackToEvents={goBackToEvents}
      />
      <EditEventDialog {...editDialogProps} />
    </DashboardLayout>
  );
}

export default EventDetailPage;
